import axios from 'services/axiosInstance';
import { EVENT_STATUS } from 'consts/event';
import { SORT, LIMIT_ALL, DEFAULT_SKIP } from 'consts/table';

const tournamentEventsCache: { [key: string]: Promise<any[]> | undefined } = {};

function stableStringify(value: any): string {
   if (value === null || typeof value !== 'object') {
      return JSON.stringify(value);
   }

   if (Array.isArray(value)) {
      return `[${value.map(stableStringify).join(',')}]`;
   }

   return `{${Object.keys(value)
      .sort()
      .map(key => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
      .join(',')}}`;
}

function getTournamentEventsCacheKey(activeSchoolId: string, tournamentId: string, filter: any): string {
   return `${activeSchoolId}:${tournamentId}:${stableStringify(filter)}`;
}

function getTournamentEvents(activeSchoolId: string, tournamentId: string, filter: any, refresh = false) {
   const cacheKey = getTournamentEventsCacheKey(activeSchoolId, tournamentId, filter);

   if (refresh) delete tournamentEventsCache[cacheKey];

   if (tournamentEventsCache[cacheKey]) {
      return tournamentEventsCache[cacheKey];
   }

   const config = {
      params: {
         filter
      }
   };

   tournamentEventsCache[cacheKey] = axios
      .get(`${window.apiBase}/public/schools/${activeSchoolId}/schools/tournaments/${tournamentId}/events`, config)
      .then(response => {
         return response.data;
      })
      .catch(error => {
         delete tournamentEventsCache[cacheKey];
         throw error;
      });

   return tournamentEventsCache[cacheKey];
}

export function getAllTournamentActiveEvents(activeSchoolId: string, tournamentId: string, where: any = {}, refresh = false) {
   const filter = {
      where: {
         status: { $in: [EVENT_STATUS.ACCEPTED, EVENT_STATUS.FINISHED] },
         ...where
      },
      limit: LIMIT_ALL,
      order: `startTime ${SORT.ASC}`,
      skip: DEFAULT_SKIP
   };

   return getTournamentEvents(activeSchoolId, tournamentId, filter, refresh);
}

export function invalidateTournamentEvents(activeSchoolId: string, tournamentId: string) {
   const prefix = `${activeSchoolId}:${tournamentId}:`;
   Object.keys(tournamentEventsCache).forEach(key => {
      if (key.startsWith(prefix)) delete tournamentEventsCache[key];
   });
}

export function getAllTournamentEvents(activeSchoolId: string, tournamentId: string, where: any = {}) {
   const filter = {
      where: {
         ...where
      },
      limit: LIMIT_ALL,
      order: `startTime ${SORT.ASC}`,
      skip: DEFAULT_SKIP
   };

   return getTournamentEvents(activeSchoolId, tournamentId, filter);
}

export function getAllTournamentOngoingEvents(activeSchoolId: string, tournamentId: string, where: any = {}) {
   const filter = {
      where: {
         status: { $in: [EVENT_STATUS.ACCEPTED] },
         ...where
      },
      limit: LIMIT_ALL,
      order: `startTime ${SORT.ASC}`,
      skip: DEFAULT_SKIP
   };

   return getTournamentEvents(activeSchoolId, tournamentId, filter);
}

export function getAllTournamentFinishedEvents(activeSchoolId: string, tournamentId: string, where: any = {}) {
   const filter = {
      where: {
         status: { $in: [EVENT_STATUS.FINISHED] },
         ...where
      },
      limit: LIMIT_ALL,
      order: `startTime ${SORT.ASC}`,
      skip: DEFAULT_SKIP
   };

   return getTournamentEvents(activeSchoolId, tournamentId, filter);
}

export function getFooterEvents(activeSchoolId: string, tournamentId: string) {
   const filter = {
      where: {
         status: { $in: [EVENT_STATUS.FINISHED] }
      },
      limit: 3,
      order: 'startTime DESC'
   };

   return getTournamentEvents(activeSchoolId, tournamentId, filter);
}
